<template>
  <div class="cer-invoice-table-shell">
    <div
      v-if="displayExploreResult.length === 0"
      class="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
    >
      No properties with predicted values greater than 0 in this category.
    </div>
    <div v-else class="cer-invoice-table-scroll">
      <v-data-table
        :headers="exploreResultHeader"
        :items="displayExploreResult"
        :disable-pagination="true"
        :items-per-page="50"
        :hide-default-footer="true"
        class="cer-invoice-data-table elevation-0 cer-invoice-table--pad"
        :expanded.sync="expanded"
        item-key="name"
        show-expand
      >
        <template  v-slot:item.index="{ item }">
          {{ displayExploreResult.indexOf(item) + 1 }}
        </template>
        <template v-slot:item.name="{ item }">
          <div
            class="cer-property-name"
            :data-tour="displayExploreResult.indexOf(item) === 0 ? 'predix-explore-row' : undefined"
          >
            <span class="cer-property-name__label" :title="item.name">{{ item.name }}</span>
            <div
              v-if="hasPropertyHelp(item.name)"
              class="cer-property-help"
              @click.stop.prevent
              @mousedown.stop.prevent
              @mouseenter="onHelpEnter($event, item)"
              @mousemove="onHelpMove($event)"
              @mouseleave="onHelpLeave"
              @focusin="onHelpEnter($event, item)"
              @focusout="onHelpLeave"
            >
              <button
                type="button"
                class="cer-property-help-btn"
                :aria-label="'About ' + item.name"
                :aria-describedby="hoveredHelpPropertyName === item.name ? 'cer-property-help-tip' : undefined"
              >
                ?
              </button>
            </div>
          </div>
        </template>
        <template v-slot:item.value="{ item }">
          <span>{{ pruneNumber(item.value) }}</span>
        </template>
        <template v-slot:item.stats="{ item }">
          <CastingExporeResultStatistics
            :min="item.stats.min"
            :max="item.stats.max"
            :val="item.value"
            :property-name="item.name"
          />
        </template>
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="cer-invoice-expanded-cell">
            <AlloyPropertyAnalytics
              class="pt-0 pb-8"
              :prpName="item.name"
              :seriesData="[
                { name: 'Min', value: item.stats.min, type: 'MIN' },
                { name: 'Avg', value: item.stats.avg, type: 'AVG' },
                { name: 'Max', value: item.stats.max, type: 'MAX' },
                { name: 'Predicted', value: item.value, type: 'PRP' },
              ]"
              :standardAlloys="standardAlloys"
              :favoriteAlloys="favoriteAlloys"
              :standardDatasetName="standardDatasetName"
              :package_id="package_id"
              :groups="item.groups"
              :standardDatasetId="standardDatasetId"
            >
            </AlloyPropertyAnalytics>
          </td>
        </template>
        <template v-slot:item.data-table-expand="{ isExpanded , expand, item }">
          <span
            class="cer-expand-wrap"
            :data-tour="displayExploreResult.indexOf(item) === 0 ? 'predix-explore-row-expand' : undefined"
            role="button"
            tabindex="0"
            @click.stop="expand(!isExpanded)"
            @keydown.enter.prevent="expand(!isExpanded)"
            @keydown.space.prevent="expand(!isExpanded)"
          >
            <v-icon small color="primary" class="cer-expand-icon">
              {{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
            </v-icon>
          </span>
        </template>
      </v-data-table>
    </div>
    <div
      v-show="hoveredHelpContent"
      id="cer-property-help-tip"
      class="cer-property-help-tip"
      role="tooltip"
      :style="helpTipStyle"
    >
      <div class="cer-property-help-tip__title">{{ hoveredHelpContent && hoveredHelpContent.title }}</div>
      <p class="cer-property-help-tip__body">{{ hoveredHelpContent && hoveredHelpContent.description }}</p>
    </div>
  </div>
</template>


<script>
import AlloyPropertyAnalytics from "@/components/Predix/AlloyPropertyAnalytics";
import CastingExporeResultStatistics from "@/components/Predix/CastingExploreResultStatistics.vue";
import { smartPruneNumber } from "@/utils/number";
import { filterPositivePredictedResults } from "@/utils/predixExploreResult";
import castingPropertyHelpMainDb from "@/constants/castingPropertyHelp.mainDb.json";
import castingPropertyHelpBenchmarkDb from "@/constants/castingPropertyHelp.benchmarkDb.json";
import { hasCastingPropertyHelp, resolveCastingPropertyHelp } from "@/utils/castingPropertyHelp";
export default {
  components: { AlloyPropertyAnalytics, CastingExporeResultStatistics },
  name: "CastingExploreResult",
  data: () => ({
    exploreResultHeader: [
      {
        text: "#",
        align: "center",
        sortable: false,
        value: "index",
        width: "3%",
      },
      {
        text: "Alloy Property",
        sortable: true,
        align: "center",
        value: "name",
        width: "36%",
      },
      {
        text: "Predicted Value",
        value: "value",
        sortable: false,
        align: "center",
        width: "10%",
      },
      { text: "Statistics", value: "stats", sortable: false, align: "center", width: "35%" },
      { text: "Benchmark", align: "center", value: "data-table-expand", width: "16%" },
    ],
    expanded: [],
    hoveredHelpPropertyName: null,
    hoveredHelpContent: null,
    helpTipStyle: {
      top: "0px",
      left: "0px",
    },
  }),

  props: {
    exploreResult: {
      type: Array,
      default: () => [],
    },
    standardAlloys: {
      type: Array,
      default: () => [],
    },
    favoriteAlloys: {
      type: Array,
      default: () => [],
    },
    standardDatasetName: {
      type: String,
      default: "",
    },
    standardDatasetId: {
      type: Number,
      default: -1,
    },
    package_id: {
      type: Number,
      default: -1,
    },
    castingPropertiesGroupId: {
      type: Number,
      default: 0,
    },
    castingPropertiesGroupName: {
      type: String,
      default: "",
    },
    /** 1 = Main DB, 2 = Benchmark DB — selects which help JSON to use */
    databaseType: {
      type: Number,
      default: 1,
    },
  },
  created() {
    this.exploreResultHeader[1].text = "Alloy " + this.castingPropertiesGroupName;
  },
  computed: {
    displayExploreResult() {
      return filterPositivePredictedResults(this.exploreResult)
    },
    castingPropertyHelpMap() {
      return Number(this.databaseType) === 2
        ? castingPropertyHelpBenchmarkDb
        : castingPropertyHelpMainDb
    },
  },
  watch: {
    displayExploreResult() {
      const visibleNames = new Set(this.displayExploreResult.map((item) => item.name))
      this.expanded = this.expanded.filter((item) => {
        const name = typeof item === "object" ? item.name : item
        return visibleNames.has(name)
      })
    },
  },
  methods: {
    expandFirstRowForTour() {
      if (!this.displayExploreResult || !this.displayExploreResult.length) return
      this.expanded = [this.displayExploreResult[0]]
    },
    hasPropertyHelp(propertyName = "") {
      return hasCastingPropertyHelp(this.castingPropertyHelpMap, propertyName);
    },
    propertyHelp(propertyName = "") {
      return resolveCastingPropertyHelp(this.castingPropertyHelpMap, propertyName);
    },
    onHelpEnter(event, item) {
      const help = this.propertyHelp(item.name);
      if (!help) {
        this.onHelpLeave();
        return;
      }
      this.hoveredHelpPropertyName = item.name;
      this.hoveredHelpContent = help;
      this.updateHelpTipPosition(event);
    },
    onHelpMove(event) {
      if (this.hoveredHelpPropertyName == null) return;
      this.updateHelpTipPosition(event);
    },
    onHelpLeave() {
      this.hoveredHelpPropertyName = null;
      this.hoveredHelpContent = null;
    },
    updateHelpTipPosition(event) {
      const offsetX = 14;
      const x = event && Number.isFinite(event.clientX) ? event.clientX + offsetX : 0;
      const y = event && Number.isFinite(event.clientY) ? event.clientY : 0;
      this.helpTipStyle = {
        top: `${y}px`,
        left: `${x}px`,
      };
    },
    getIndicatorPosition(min, max, value, length) {
      const bias = 10;
      if (min == max || value == min) {
        return bias;
      } else if (value > max) {
        return bias + length;
      } else if (value < min) {
        return bias;
      } else {
        return bias + (value * length) / (max - min);
      }
    },
    pruneNumber(num) {
      return smartPruneNumber(num);
    },
  },
};
</script>
<style scoped lang="scss">
/*
  Light + dark:
  - Light: default rules (InvoicesTable-style neutrals).
  - Dark: `html.dark` (Tailwind, synced in App.vue) AND `.v-application.theme--dark` (Vuetify).
*/

/* Shell — always visible so expanding a row does not change clipping/layout mode */
.cer-invoice-table-shell {
  --cer-cell-px: 0.5rem;
  --cer-cell-py: 0.875rem;
  --cer-expand-hit-size: 2.75rem;
  background-color: var(--color-white);
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  border-radius: 0 0 var(--radius-xl) var(--radius-xl);
  position: relative;
  overflow: visible;
  min-width: 0;
  max-width: 100%;
}

html.dark .cer-invoice-table-shell,
.v-application.theme--dark .cer-invoice-table-shell {
  background-color: var(--color-gray-800);
}

.cer-invoice-table-scroll {
  overflow-x: hidden;
  overflow-y: visible;
  max-width: 100%;
  min-width: 0;
  -webkit-overflow-scrolling: touch;
}

/* Moderate horizontal padding (avoid pl-9 + wide table → horizontal scroll) */
::v-deep .cer-invoice-data-table.cer-invoice-table--pad .v-data-table__wrapper {
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}

@media (min-width: 960px) {
  ::v-deep .cer-invoice-data-table.cer-invoice-table--pad .v-data-table__wrapper {
    padding-left: 0.875rem;
    padding-right: 0.875rem;
  }
}

/* Table root text — match InvoicesTable: light gray-800 / dark gray-300 */
::v-deep .cer-invoice-data-table.v-data-table {
  background-color: transparent !important;
  max-width: 100%;
  min-width: 0;
}

.v-application.theme--light ::v-deep .cer-invoice-data-table.v-data-table {
  color: var(--color-gray-800) !important;
}

html.dark ::v-deep .cer-invoice-data-table.v-data-table,
.v-application.theme--dark ::v-deep .cer-invoice-data-table.v-data-table {
  color: var(--color-gray-300) !important;
}

/* Keep table inside viewport — Vuetify defaults to overflow-x: auto on wrapper */
::v-deep .cer-invoice-data-table .v-data-table__wrapper {
  overflow-x: hidden !important;
  overflow-y: visible !important;
  max-height: none !important;
  width: 100%;
  max-width: 100%;
}

/* Vuetify: fixed-height / fixed-header wrappers use overflow-y: auto — kills single-page scroll in expanded rows */
::v-deep .cer-invoice-data-table.v-data-table--fixed-height .v-data-table__wrapper,
::v-deep .cer-invoice-data-table.v-data-table--fixed-header .v-data-table__wrapper {
  overflow-y: visible !important;
  overflow-x: hidden !important;
  max-height: none !important;
  height: auto !important;
}

::v-deep .cer-invoice-data-table tbody {
  overflow: visible !important;
}

::v-deep .cer-invoice-data-table table {
  width: 100%;
  max-width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

::v-deep .cer-invoice-data-table thead th,
::v-deep .cer-invoice-data-table tbody td {
  box-sizing: border-box !important;
}

/* Lock column widths so expanded benchmark content never rebalances the main row cells */
::v-deep .cer-invoice-data-table thead th:nth-child(1),
::v-deep .cer-invoice-data-table tbody td:nth-child(1) {
  width: 3% !important;
}

::v-deep .cer-invoice-data-table thead th:nth-child(2),
::v-deep .cer-invoice-data-table tbody td:nth-child(2) {
  width: 36% !important;
}

::v-deep .cer-invoice-data-table thead th:nth-child(3),
::v-deep .cer-invoice-data-table tbody td:nth-child(3) {
  width: 10% !important;
}

::v-deep .cer-invoice-data-table thead th:nth-child(4),
::v-deep .cer-invoice-data-table tbody td:nth-child(4) {
  width: 35% !important;
}

::v-deep .cer-invoice-data-table thead th:nth-child(5),
::v-deep .cer-invoice-data-table tbody td:nth-child(5) {
  width: 16% !important;
}

/* Column 1 (#): keep body pinned to top when expanded content appears below */
::v-deep .cer-invoice-data-table thead th:nth-child(1) {
  text-align: center !important;
  vertical-align: middle !important;
  padding-left: 0.25rem !important;
  padding-right: 0.25rem !important;
}

::v-deep .cer-invoice-data-table tbody td:nth-child(1) {
  text-align: center !important;
  vertical-align: middle !important;
  padding-left: 0.25rem !important;
  padding-right: 0.25rem !important;
}

/* thead — extra vertical padding */
::v-deep .cer-invoice-data-table thead th {
  font-size: 0.75rem !important;
  font-family: var(--font-inter), var(--font-sans) !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.03em;
  padding: 1rem var(--cer-cell-px) !important;
  white-space: nowrap !important;
  vertical-align: middle !important;
  height: auto !important;
}

::v-deep .cer-invoice-data-table thead th:first-child {
  padding-left: 0.5rem !important;
}

::v-deep .cer-invoice-data-table thead th:last-child {
  padding-left: 0.25rem !important;
  padding-right: 0.25rem !important;
  text-align: center !important;
  white-space: normal !important;
  line-height: 1.2 !important;
  word-break: break-word !important;
}

/* Column 2 (Alloy name): centered, wrap within column */
::v-deep .cer-invoice-data-table thead th:nth-child(2) {
  min-width: 0 !important;
  padding-left: 0.5rem !important;
  padding-right: 0.5rem !important;
  text-align: center !important;
  vertical-align: middle !important;
}

::v-deep .cer-invoice-data-table thead th:nth-child(2) {
  white-space: normal !important;
}

::v-deep .cer-invoice-data-table tbody td:nth-child(2) {
  white-space: normal !important;
  word-break: break-word !important;
  min-width: 0 !important;
  padding-left: 0.5rem !important;
  padding-right: 0.5rem !important;
  text-align: center !important;
  vertical-align: middle !important;
}

/* Column 3 (Predicted value): centered under header */
::v-deep .cer-invoice-data-table thead th:nth-child(3) {
  min-width: 0 !important;
  padding-left: 0.375rem !important;
  padding-right: 0.375rem !important;
  text-align: center !important;
  vertical-align: middle !important;
}

::v-deep .cer-invoice-data-table tbody td:nth-child(3) {
  white-space: nowrap !important;
  min-width: 0 !important;
  padding-left: 0.375rem !important;
  padding-right: 0.375rem !important;
  text-align: center !important;
  vertical-align: middle !important;
}

::v-deep .cer-invoice-data-table tbody td:nth-child(3) > span {
  display: inline-block;
  width: 100%;
  text-align: center !important;
}

/* Column 4 (Statistics): bar uses full cell width (progress sits in the wide column) */
::v-deep .cer-invoice-data-table thead th:nth-child(4) {
  min-width: 0 !important;
  padding-left: 0.5rem !important;
  padding-right: 0.5rem !important;
  text-align: center !important;
  vertical-align: middle !important;
}

::v-deep .cer-invoice-data-table tbody td:nth-child(4) {
  min-width: 0 !important;
  padding-left: 0.5rem !important;
  padding-right: 0.5rem !important;
  text-align: center !important;
  vertical-align: middle !important;
}

::v-deep .cer-invoice-data-table tbody td:nth-child(4) .cer-stat {
  width: 100% !important;
  max-width: 100% !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
}

/* Light: bg-gray-50, borders gray-100 — only in light theme (avoid “white” header in dark) */
.v-application.theme--light ::v-deep .cer-invoice-data-table thead th {
  color: var(--color-gray-500) !important;
  background-color: var(--color-gray-50) !important;
  border-top: 1px solid var(--color-gray-100) !important;
  border-bottom: 1px solid var(--color-gray-100) !important;
}

/*
  Dark: match InvoicesTable thead — dark:text-gray-400 dark:bg-gray-900/20 dark:border-gray-700/60
  Blend 20% gray-900 over gray-800 shell so header reads darker than body rows.
*/
html.dark ::v-deep .cer-invoice-data-table thead th,
.v-application.theme--dark ::v-deep .cer-invoice-data-table thead th {
  color: var(--color-gray-400) !important;
  background-color: color-mix(in srgb, var(--color-gray-900) 20%, var(--color-gray-800)) !important;
  border-top-color: color-mix(in srgb, var(--color-gray-700) 60%, transparent) !important;
  border-bottom-color: color-mix(in srgb, var(--color-gray-700) 60%, transparent) !important;
  border-top-width: 1px !important;
  border-bottom-width: 1px !important;
  border-style: solid !important;
}

/* tbody — divide-y + clear Vuetify row fill (InvoicesTable) */
.v-application.theme--light ::v-deep .cer-invoice-data-table tbody tr {
  border-bottom: 1px solid var(--color-gray-100);
  background-color: transparent !important;
}

html.dark ::v-deep .cer-invoice-data-table tbody tr,
.v-application.theme--dark ::v-deep .cer-invoice-data-table tbody tr {
  border-bottom: 1px solid color-mix(in srgb, var(--color-gray-700) 60%, transparent);
  background-color: transparent !important;
}

.v-application.theme--light ::v-deep .cer-invoice-data-table tbody tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
  background-color: color-mix(in srgb, var(--color-gray-100) 80%, transparent) !important;
}

html.dark ::v-deep .cer-invoice-data-table tbody tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper),
.v-application.theme--dark ::v-deep .cer-invoice-data-table tbody tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
  background-color: color-mix(in srgb, var(--color-gray-700) 25%, transparent) !important;
}

::v-deep .cer-invoice-data-table tbody tr:not(.v-data-table__expanded__content) > td {
  padding-top: var(--cer-cell-py) !important;
  padding-bottom: var(--cer-cell-py) !important;
}

::v-deep .cer-invoice-data-table tbody td {
  font-size: var(--text-sm) !important;
  font-family: var(--font-inter), var(--font-sans) !important;
  font-weight: 400 !important;
  line-height: var(--text-sm--line-height) !important;
  letter-spacing: normal !important;
  border-bottom: none !important;
  padding: var(--cer-cell-py) 0.375rem !important;
  text-align: center !important;
  vertical-align: middle !important;
}

/* Undo Vuetify heading classes if any column still carries them */
::v-deep .cer-invoice-data-table tbody td.text-h5,
::v-deep .cer-invoice-data-table tbody td .text-h5 {
  font-size: var(--text-sm) !important;
  font-weight: 400 !important;
  line-height: var(--text-sm--line-height) !important;
  letter-spacing: normal !important;
}

.v-application.theme--light ::v-deep .cer-invoice-data-table tbody td {
  color: var(--color-gray-800) !important;
}

html.dark ::v-deep .cer-invoice-data-table tbody td,
.v-application.theme--dark ::v-deep .cer-invoice-data-table tbody td {
  color: var(--color-gray-300) !important;
}

::v-deep .cer-invoice-data-table tbody td:last-child {
  min-width: calc(var(--cer-expand-hit-size) + 0.5rem) !important;
  padding: var(--cer-cell-py) 0.25rem !important;
  text-align: center !important;
  vertical-align: middle !important;
}

::v-deep .cer-invoice-data-table .cer-expand-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--cer-expand-hit-size);
  height: var(--cer-expand-hit-size);
  min-width: var(--cer-expand-hit-size);
  min-height: var(--cer-expand-hit-size);
  border-radius: 9999px;
  vertical-align: middle;
  cursor: pointer;
  user-select: none;
}

::v-deep .cer-invoice-data-table .cer-expand-icon {
  font-size: 1.25rem !important;
  line-height: 1 !important;
  pointer-events: none;
}

::v-deep .cer-invoice-data-table .v-data-table__expanded__row td {
  padding: 0 !important;
  border-bottom: none !important;
}

.v-application.theme--light ::v-deep .cer-invoice-data-table tbody tr.v-data-table__expanded:not(.v-data-table__expanded__content) {
  border-bottom: 1px solid var(--color-gray-100) !important;
}

html.dark ::v-deep .cer-invoice-data-table tbody tr.v-data-table__expanded:not(.v-data-table__expanded__content),
.v-application.theme--dark ::v-deep .cer-invoice-data-table tbody tr.v-data-table__expanded:not(.v-data-table__expanded__content) {
  border-bottom: 1px solid color-mix(in srgb, var(--color-gray-700) 60%, transparent) !important;
}

::v-deep .cer-invoice-data-table .v-data-table__expanded__content {
  box-shadow: none !important;
}

::v-deep .cer-invoice-data-table tr.v-data-table__expanded__content {
  overflow: visible !important;
}

::v-deep .cer-invoice-data-table tr.v-data-table__expanded__content > td {
  overflow: visible !important;
  max-height: none !important;
}

.cer-invoice-expanded-cell {
  padding: 0 0.625rem 0.75rem !important;
  vertical-align: top !important;
  overflow: visible !important;
  max-height: none !important;
}

/* Expanded benchmark block: no inner scroll — only the page scrolls */
::v-deep .cer-invoice-expanded-cell > * {
  overflow: visible !important;
  max-height: none !important;
}

::v-deep .cer-invoice-expanded-cell .row,
::v-deep .cer-invoice-expanded-cell .col {
  overflow: visible !important;
  max-height: none !important;
}

::v-deep .cer-invoice-expanded-cell .alloy-property-analytics {
  overflow: visible !important;
  max-height: none !important;
  min-width: 0;
  width: 100%;
}

.v-application.theme--light .cer-invoice-expanded-cell {
  background-color: color-mix(in srgb, var(--color-gray-50) 50%, transparent);

}

html.dark .cer-invoice-expanded-cell,
.v-application.theme--dark .cer-invoice-expanded-cell {
  background-color: color-mix(in srgb, var(--color-gray-900) 35%, transparent);

}

.cer-property-name {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  max-width: 100%;
  min-width: 0;
}

.cer-property-name__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cer-property-help {
  flex-shrink: 0;
}

.cer-property-help-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.375rem;
  height: 1.375rem;
  border-radius: 9999px;
  border: 1px solid rgba(209, 213, 219, 0.95);
  background-color: rgb(243, 244, 246);
  color: rgb(55, 65, 81);
  font-size: 0.6875rem;
  font-weight: 700;
  line-height: 1;
  cursor: help;
  transition: background-color 150ms ease, border-color 150ms ease, color 150ms ease;
}

.cer-property-help-btn:hover,
.cer-property-help:focus-within .cer-property-help-btn {
  background-color: rgb(229, 231, 235);
  border-color: rgba(3, 147, 168, 0.45);
  color: rgb(17, 24, 39);
}

html.dark .cer-property-help-btn,
.v-application.theme--dark .cer-property-help-btn {
  border-color: rgba(255, 255, 255, 0.12);
  background-color: rgba(255, 255, 255, 0.06);
  color: rgb(209, 213, 219);
}

html.dark .cer-property-help-btn:hover,
html.dark .cer-property-help:focus-within .cer-property-help-btn,
.v-application.theme--dark .cer-property-help-btn:hover,
.v-application.theme--dark .cer-property-help:focus-within .cer-property-help-btn {
  background-color: rgba(255, 255, 255, 0.12);
  border-color: rgba(3, 147, 168, 0.55);
  color: rgb(243, 244, 246);
}

.cer-property-help-tip {
  position: fixed;
  z-index: 4000;
  transform: translateY(-50%);
  width: min(16.5rem, 60vw);
  max-width: calc(100vw - 1.5rem);
  padding: 0.75rem 0.875rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(229, 231, 235, 0.95);
  background-color: rgba(255, 255, 255, 0.98);
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 4px 6px -4px rgba(0, 0, 0, 0.08);
  text-align: left;
  pointer-events: none;
}

html.dark .cer-property-help-tip,
.v-application.theme--dark .cer-property-help-tip {
  border-color: rgba(255, 255, 255, 0.12);
  background-color: rgba(31, 41, 55, 0.98);
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.45);
}

.cer-property-help-tip__title {
  margin-bottom: 0.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: rgb(17, 24, 39);
}

html.dark .cer-property-help-tip__title,
.v-application.theme--dark .cer-property-help-tip__title {
  color: rgb(243, 244, 246);
}

.cer-property-help-tip__body {
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.45;
  color: rgb(75, 85, 99);
  white-space: normal;
}

html.dark .cer-property-help-tip__body,
.v-application.theme--dark .cer-property-help-tip__body {
  color: rgb(156, 163, 175);
}

::v-deep .cer-invoice-data-table tbody td:nth-child(2) {
  overflow: visible !important;
}
</style>
